<template>
  <div class="sf-image-editor">
    <v-menu offset-y
            v-bind:disabled="busy">
      <template v-slot:activator="{ on, attrs }">
        <div class="sf-image-editor__thumb"
             v-bind="attrs"
             v-bind:class="{ 'sf-image-editor__thumb--empty': product.imageUrl == null }"
             v-bind:title="product.imageUrl != null ? $t('tooltipReplace') : $t('tooltipUpload')"
             v-on="on">
          <v-img contain
                 height="56"
                 width="56"
                 v-bind:src="product.imageUrl"
                 v-if="product.imageUrl != null" />
          <v-icon color="primary"
                  v-else>ti-photo-plus</v-icon>
          <div class="sf-image-editor__overlay"
               v-if="busy">
            <v-progress-circular color="primary"
                                 size="24"
                                 width="2"
                                 indeterminate />
          </div>
          <div class="sf-image-editor__badge"
               v-else-if="product.imageUrl != null">
            <v-icon size="14">ti-pencil</v-icon>
          </div>
        </div>
      </template>
      <v-list dense>
        <v-list-item v-on:click="choose">
          <v-list-item-icon class="mr-3"><v-icon>ti-upload</v-icon></v-list-item-icon>
          <v-list-item-title>{{ product.imageUrl != null ? $t('replace') : $t('upload') }}</v-list-item-title>
        </v-list-item>
        <v-list-item v-if="product.imageUrl != null"
                     v-on:click="confirmRemove">
          <v-list-item-icon class="mr-3"><v-icon color="error">ti-trash</v-icon></v-list-item-icon>
          <v-list-item-title class="error--text">{{ $t('remove') }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <input accept="image/*"
           class="sf-image-editor__input"
           ref="picker"
           type="file"
           v-on:change="onFileChosen">

    <image-crop-dialog v-bind:file="file"
                       v-bind:title="`${$t('cropTitle')} – ${product.id}`"
                       v-model="cropDialog"
                       v-on:cancel="clearFile"
                       v-on:confirm="upload" />

    <confirm-dialog ref="removeDialog"
                    v-bind:cancel-button-label="$t('cancel')"
                    v-bind:confirm-button-label="$t('remove')"
                    v-bind:message="$t('removeMessage', { id: product.id })"
                    v-bind:title="$t('removeTitle')"
                    v-model="removeDialog" />

    <v-snackbar color="error"
                right
                top
                timeout="6000"
                v-model="errorSnackbar">
      {{ errorMessage }}
    </v-snackbar>
  </div>
</template>

<script>
  import ConfirmDialog from '@/components/common/ConfirmDialog'
  import ImageCropDialog from '@/components/common/ImageCropDialog'

  /**
   * Thumbnail mit Menü „Bild hochladen/ersetzen“ und „Entfernen“ für die Design-Vorschau.
   * Lieferanten übergeben ihren Einladungscode, Administratoren nichts (dann wird das Access-Token verwendet).
   * Nach erfolgreichem Upload wird `product.imageUrl` direkt am übergebenen Objekt gesetzt.
   */
  export default {
    components: {
      ConfirmDialog,
      ImageCropDialog
    },
    data: () => ({
      busy: false,
      cropDialog: false,
      errorMessage: null,
      errorSnackbar: false,
      file: null,
      removeDialog: false
    }),
    methods: {
      choose() {
        this.$refs.picker.value = ''
        this.$refs.picker.click()
      },
      clearFile() {
        this.file = null
      },
      async confirmRemove() {
        const confirmed = await new Promise(resolve => this.$refs.removeDialog.open(resolve))

        if (!confirmed) {
          return
        }

        this.busy = true

        try {
          await this.$store.dispatch('deleteProductImage', { productId: this.product.id, invitationCode: this.invitationCode })

          this.$set(this.product, 'imageUrl', null)
          this.$emit('changed', { productId: this.product.id, imageUrl: null })
        } catch (e) {
          this.showError(e)
        } finally {
          this.busy = false
        }
      },
      onFileChosen(e) {
        const file = e.target.files != null ? e.target.files[0] : null

        if (file == null) {
          return
        }

        this.file = file
        this.cropDialog = true
      },
      showError(e) {
        this.errorMessage = e != null && e.message ? e.message : this.$t('failed')
        this.errorSnackbar = true
      },
      async upload(blob) {
        this.busy = true

        try {
          const result = await this.$store.dispatch('uploadProductImage', { productId: this.product.id, blob, invitationCode: this.invitationCode })

          this.$set(this.product, 'imageUrl', result.url)
          this.$emit('changed', { productId: this.product.id, imageUrl: result.url })
        } catch (e) {
          this.showError(e)
        } finally {
          this.busy = false
          this.file = null
        }
      }
    },
    name: 'ProductImageEditor',
    props: {
      invitationCode: String,
      product: {
        required: true,
        type: Object
      }
    }
  }
</script>

<style scoped>
  .sf-image-editor__thumb {
    align-items: center;
    background: #fff;
    border: 1px solid var(--sf-border);
    border-radius: 10px;
    cursor: pointer;
    display: flex;
    height: 62px;
    justify-content: center;
    overflow: hidden;
    padding: 2px;
    position: relative;
    width: 62px;
  }

  .sf-image-editor__thumb--empty {
    border-style: dashed;
    border-color: var(--sf-border-strong, #c4c9d4);
  }

  .sf-image-editor__thumb:hover {
    border-color: var(--sf-primary);
  }

  .sf-image-editor__overlay {
    align-items: center;
    background: rgba(255, 255, 255, .8);
    display: flex;
    inset: 0;
    justify-content: center;
    position: absolute;
  }

  .sf-image-editor__badge {
    align-items: center;
    background: var(--sf-primary);
    border-radius: 50%;
    bottom: 2px;
    color: #fff;
    display: flex;
    height: 20px;
    justify-content: center;
    position: absolute;
    right: 2px;
    width: 20px;
  }

  .sf-image-editor__badge .v-icon {
    color: #fff;
  }

  .sf-image-editor__input {
    display: none;
  }
</style>

<i18n>
  {
    "de": {
      "cancel": "Abbrechen",
      "cropTitle": "Artikelbild",
      "failed": "Das Bild konnte nicht gespeichert werden.",
      "remove": "Bild entfernen",
      "removeMessage": "Soll das Bild für Artikel {id} entfernt werden? Kunden sehen dann kein Artikelbild mehr.",
      "removeTitle": "Bild entfernen",
      "replace": "Bild ersetzen…",
      "tooltipReplace": "Artikelbild ersetzen oder entfernen",
      "tooltipUpload": "Artikelbild hochladen – wird automatisch quadratisch zugeschnitten",
      "upload": "Bild hochladen…"
    },
    "en": {
      "cancel": "Cancel",
      "cropTitle": "Product image",
      "failed": "The image could not be saved.",
      "remove": "Remove image",
      "removeMessage": "Remove the image of product {id}? Customers will no longer see a product image.",
      "removeTitle": "Remove image",
      "replace": "Replace image…",
      "tooltipReplace": "Replace or remove product image",
      "tooltipUpload": "Upload product image – it will be cropped to a square automatically",
      "upload": "Upload image…"
    }
  }
</i18n>
